// ============================================================================
//  demoStorage.js — the storage seam, demo edition.
//
//  Same exported surface as src/lib/storage.js, backed by an in-memory copy
//  of the fictional seed instead of Supabase. Writes mutate the copy so the
//  demo feels alive; a refresh resets the world. Nothing here ever touches
//  the network, the real database, or real men's data.
//
//  The visibility and authority rules here MIRROR the production SQL. If they
//  drift, the demo lies about the product — a prospect clicking "Change role"
//  as the Team Admin persona should get exactly the refusal the real app gives,
//  which is why the guards below throw the same message strings as 0005.
//
//    can_read_man   -> visible()
//    can_manage_man -> canManage()
//    private_details -> stripped from marks/photos for peers, not for leaders
// ============================================================================

import { buildDemoSeed } from "./demoData.js";
import { getPersonaId } from "./demoMode.js";

let world = null;
function getWorld() {
  if (!world) world = buildDemoSeed();
  return world;
}

const RANK = { mentee: 1, mentor: 2, admin: 3, super_admin: 4 };
const rankOf = (p) => (p && !p.is_suspended ? (RANK[p.role] ?? 0) : 0);

/** Teams this man leads. Mirrors led_group_ids(): suspended or demoted = none. */
function ledGroups(w, userId) {
  const me = w.people[userId];
  if (!me || me.is_suspended || !["mentor", "admin"].includes(me.role)) return [];
  return Object.keys(w.leaders).filter((gid) => (w.leaders[gid] ?? []).includes(userId));
}

function isSuper(w, userId) {
  const me = w.people[userId];
  return !!me && !me.is_suspended && me.role === "super_admin";
}

/** Mirrors i_lead(target). */
function iLead(w, userId, targetId) {
  if (isSuper(w, userId)) return true;
  const t = w.people[targetId];
  if (!t?.group_id) return false;
  return ledGroups(w, userId).includes(t.group_id);
}

/** Mirrors can_read_man(target). */
function visible(w, userId, target) {
  if (target.id === userId) return true;              // always yourself, even suspended
  if (iLead(w, userId, target.id)) return true;
  const me = w.people[userId];
  return !!me && !me.is_suspended && !target.is_suspended
    && !!target.group_id && target.group_id === me.group_id;
}

/** Mirrors can_manage_man(target). */
function canManage(w, userId, targetId) {
  const me = w.people[userId];
  const t = w.people[targetId];
  if (!me || !t || userId === targetId) return false;
  if (rankOf(me) < 2) return false;
  if (rankOf(t) >= rankOf(me)) return false;
  if (isSuper(w, userId)) return true;
  return !!t.group_id && ledGroups(w, userId).includes(t.group_id);
}

function deny(msg) { throw new Error(msg); }

/* ---------- read ---------------------------------------------------------- */

export async function loadState(currentUserId) {
  const w = getWorld();

  const people = {};
  for (const p of Object.values(w.people)) {
    if (visible(w, currentUserId, p)) people[p.id] = { ...p };
  }

  const myLed = ledGroups(w, currentUserId);
  const superUser = isSuper(w, currentUserId);

  // Teams the viewer can see: his own, plus every one he leads (all, if super).
  const myTeam = w.people[currentUserId]?.group_id ?? null;
  const groups = {};
  for (const [id, g] of Object.entries(w.groups)) {
    if (superUser || id === myTeam || myLed.includes(id)) groups[id] = { ...g };
  }
  const leaders = {};
  for (const id of Object.keys(groups)) leaders[id] = [...(w.leaders[id] ?? [])];

  const goals = {};
  const marks = {};
  for (const ownerId of Object.keys(people)) {
    const leaderView = ownerId === currentUserId || iLead(w, currentUserId, ownerId);
    goals[ownerId] = (w.goals[ownerId] ?? []).map((g) => {
      // A private goal stays fully visible; only its updates are withheld,
      // and only from peers. This is the marks/photos policy in 0004.
      const hide = !!g.private_details && !leaderView;
      return {
        ...g,
        smart: { ...g.smart },
        photos: hide ? [] : g.photos.map((ph) => ({ ...ph })),
      };
    });
    for (const g of goals[ownerId]) {
      const hide = !!g.private_details && !leaderView;
      marks[g.id] = hide
        // Keep pct and rag so the roster percentage still adds up; drop the
        // words. Matching what a peer can see in production.
        ? (w.marks[g.id] ?? []).map((m) => ({
            month: m.month, pct: m.pct, rag: m.rag, ts: m.ts,
            note: "", blockers: "", commitment: "", commitment_kept: null,
          }))
        : (w.marks[g.id] ?? []).map((m) => ({ ...m }));
    }
  }

  return { people, goals, marks, groups, leaders, myLedGroupIds: myLed, currentUserId };
}

/* ---------- team + roster management ------------------------------------- */
//  Same guards and, deliberately, the same message strings as 0005.

// The acting man is whichever persona the switcher is on — the same value
// DemoShell fabricates the session from, so there is nothing to keep in sync.
function caller() {
  return { w: getWorld(), id: getPersonaId() };
}

export async function createTeam(name, maxMembers = 15) {
  const { w, id } = caller();
  if (rankOf(w.people[id]) < 3) deny("forbidden: admins and super admins only");
  if (!name?.trim()) deny("team name required");
  const gid = `demo-group-${Math.random().toString(36).slice(2, 7)}`;
  w.groups[gid] = { id: gid, name: name.trim(), max_members: maxMembers };
  w.leaders[gid] = [id];
  return gid;
}

export async function renameTeam(groupId, name) {
  const { w, id } = caller();
  if (!name?.trim()) deny("team name required");
  if (!w.groups[groupId]) deny("team not found");
  if (!isSuper(w, id) && !ledGroups(w, id).includes(groupId)) {
    deny("forbidden: you do not lead this team");
  }
  w.groups[groupId].name = name.trim();
}

export async function setTeamCapacity(groupId, maxMembers) {
  const { w, id } = caller();
  if (rankOf(w.people[id]) < 3) deny("forbidden: admins and super admins only");
  if (!isSuper(w, id) && !ledGroups(w, id).includes(groupId)) {
    deny("forbidden: you do not lead this team");
  }
  const count = Object.values(w.people).filter((p) => p.group_id === groupId && !p.is_suspended).length;
  if (maxMembers < count) deny(`team already has ${count} men; cannot set the cap below that`);
  w.groups[groupId].max_members = maxMembers;
}

export async function assignLeader(userId, groupId) {
  const { w, id } = caller();
  if (!isSuper(w, id) && !ledGroups(w, id).includes(groupId)) {
    deny("forbidden: you do not lead this team");
  }
  if (!canManage(w, id, userId)) deny("forbidden: you do not manage this man");
  if (rankOf(w.people[userId]) < 2) deny("only mentors and admins can lead a team");
  if (!w.leaders[groupId]) w.leaders[groupId] = [];
  if (!w.leaders[groupId].includes(userId)) w.leaders[groupId].push(userId);
}

export async function unassignLeader(userId, groupId) {
  const { w, id } = caller();
  if (!isSuper(w, id) && !ledGroups(w, id).includes(groupId)) {
    deny("forbidden: you do not lead this team");
  }
  if (userId === id && !isSuper(w, id)) deny("cannot remove yourself as leader");
  if (!isSuper(w, id) && (w.leaders[groupId] ?? []).length <= 1) {
    deny("team would have no leader");
  }
  w.leaders[groupId] = (w.leaders[groupId] ?? []).filter((x) => x !== userId);
}

export async function moveManToTeam(userId, groupId) {
  const { w, id } = caller();
  if (userId === id) deny("cannot move yourself");
  if (!canManage(w, id, userId)) deny("forbidden: you do not manage this man");
  if (groupId == null) {
    if (!isSuper(w, id)) deny("forbidden: super admin only");
  } else {
    if (!w.groups[groupId]) deny("team not found");
    if (!isSuper(w, id) && !ledGroups(w, id).includes(groupId)) {
      deny("forbidden: you do not lead the destination team");
    }
    if (w.people[userId].group_id !== groupId) {
      const count = Object.values(w.people)
        .filter((p) => p.group_id === groupId && !p.is_suspended).length;
      const cap = w.groups[groupId].max_members ?? 15;
      if (count >= cap) deny(`team is full (${count} of ${cap} men)`);
    }
  }
  w.people[userId].group_id = groupId;
}

export async function setManRole(userId, role) {
  const { w, id } = caller();
  if (userId === id) deny("cannot change your own role");
  if (!canManage(w, id, userId)) deny("forbidden: you do not manage this man");
  if ((RANK[role] ?? 0) >= rankOf(w.people[id])) {
    deny("cannot grant a role at or above your own");
  }
  w.people[userId].role = role;
  if ((RANK[role] ?? 0) < 2) {
    for (const gid of Object.keys(w.leaders)) {
      w.leaders[gid] = w.leaders[gid].filter((x) => x !== userId);
    }
  }
}

/* ---------- profile ------------------------------------------------------- */

/* ---------- visibility notice --------------------------------------------- */

// The real app records this on the profile row. The demo has no database and
// hard-reloads the page on every persona switch, so keep it in sessionStorage:
// a prospect sees the notice once on arrival, not once per persona.
const NOTICE_KEY = "milemarker.demo_visibility_ack";

export function getNoticeAck() {
  try { return sessionStorage.getItem(NOTICE_KEY); } catch { return null; }
}

export async function acceptVisibilityNotice(userId) {
  const p = getWorld().people[userId];
  const now = new Date().toISOString();
  if (p) p.accepted_visibility_notice_at = now;
  try { sessionStorage.setItem(NOTICE_KEY, now); } catch { /* private mode */ }
}

export async function updateProfile(userId, { name, photo }) {
  const p = getWorld().people[userId];
  if (!p) return;
  p.name = name;
  if (photo !== undefined) p.photo = photo || "";
}

/* ---------- goals --------------------------------------------------------- */

export async function upsertGoal(ownerId, goal) {
  const w = getWorld();
  if (!w.goals[ownerId]) w.goals[ownerId] = [];
  if (goal.id) {
    const list = w.goals[ownerId];
    const i = list.findIndex((g) => g.id === goal.id);
    if (i >= 0) list[i] = { ...list[i], ...goal, smart: { ...goal.smart } };
    return goal.id;
  }
  const id = `dg-new-${Math.random().toString(36).slice(2, 9)}`;
  w.goals[ownerId].push({ ...goal, id, private_details: !!goal.private_details, photos: [] });
  return id;
}

export async function deleteGoal(goalId) {
  const w = getWorld();
  for (const ownerId of Object.keys(w.goals)) {
    w.goals[ownerId] = w.goals[ownerId].filter((g) => g.id !== goalId);
  }
  delete w.marks[goalId];
}

/* ---------- mile markers -------------------------------------------------- */

export async function upsertMark(goalId, mark) {
  const w = getWorld();
  if (!w.marks[goalId]) w.marks[goalId] = [];
  const list = w.marks[goalId];
  const i = list.findIndex((m) => m.month === mark.month);
  const row = { ...mark, commitment_kept: null, ts: Date.now() };
  if (i >= 0) list[i] = { ...list[i], ...row };
  else list.push(row);
}

/* ---------- photos -------------------------------------------------------- */

export async function addPhoto(ownerId, goalId, file) {
  // No upload — the browser shows the local file directly.
  const w = getWorld();
  const photo = {
    id: `dp-${Math.random().toString(36).slice(2, 9)}`,
    dataUrl: URL.createObjectURL(file),
    storage_path: "",
    ts: Date.now(),
  };
  const g = (w.goals[ownerId] ?? []).find((x) => x.id === goalId);
  if (g) g.photos.push({ ...photo });
  return photo;
}

export async function signPhotoUrls() {
  // Demo photos already carry a usable blob/data URL on the photo record, so
  // there is nothing to sign. Returning an empty map makes GoalCard fall back
  // to the dataUrl it already has.
  return {};
}

export async function deletePhoto(photoId) {
  const w = getWorld();
  for (const ownerId of Object.keys(w.goals)) {
    for (const g of w.goals[ownerId]) {
      g.photos = g.photos.filter((ph) => ph.id !== photoId);
    }
  }
}

/* ---------- admin: suspend / unsuspend ----------------------------------- */

export async function setProfileSuspended(targetId, isSuspended) {
  const p = getWorld().people[targetId];
  if (p) p.is_suspended = isSuspended;
}

/* ---------- coach --------------------------------------------------------- */

const COACH_FOLLOWUPS = [
  "Good push-back. One more thought: put the *when* on your calendar today. A goal without a scheduled slot is a wish. What day and time will this happen each week?",
  "That's the right instinct. Keep the measure simple enough that you can answer \"did I do it?\" with a yes or no at your Mile Marker meeting. You've got this - now go write it down.",
  "Love it. Last thing: tell one brother in your group about this goal before the day ends. Accountability announced is accountability doubled.",
];
export async function askCoach({ goal, messages }) {
  // Canned, goal-aware responses. The live app calls an Anthropic-backed Edge
  // Function here; the demo stays offline by design.
  await new Promise((r) => setTimeout(r, 700));
  const userTurns = (messages ?? []).filter((m) => m.role === "user").length;
  if (userTurns <= 1) {
    const title = goal?.title?.trim() || "this goal";
    const hasMeasure = !!goal?.smart?.m?.trim();
    const hasTime = !!goal?.smart?.t?.trim();
    let reply =
      `I like where you're headed with *${title}*. Here's what I see:\n\n` +
      `**Specific** - clear enough that another man would know what you mean. Good.\n` +
      (hasMeasure
        ? `**Measurable** - "${goal.smart.m.trim()}" gives you a number to hit. That's what makes the monthly check-in honest.\n`
        : `**Measurable** - this is the gap. Add a number: how many times, how much, by when? You can't celebrate what you can't count.\n`) +
      (hasTime
        ? `**Time-bound** - "${goal.smart.t.trim()}" puts a clock on it. Well done.\n`
        : `**Time-bound** - give it a deadline or a rhythm. "Someday" is where goals go to die.\n`) +
      `\nWhat feels like the hardest part of actually doing this one?`;
    reply += `\n\n*(Demo note: in the live app this is a real AI coach. These are sample responses.)*`;
    return { reply };
  }
  return { reply: COACH_FOLLOWUPS[(userTurns - 2) % COACH_FOLLOWUPS.length] };
}

/* ---------- invite -------------------------------------------------------- */

export async function inviteMan({ group_id } = {}) {
  await new Promise((r) => setTimeout(r, 600));
  const { w } = caller();
  if (group_id && w.groups[group_id]) {
    const count = Object.values(w.people)
      .filter((p) => p.group_id === group_id && !p.is_suspended).length;
    const cap = w.groups[group_id].max_members ?? 15;
    if (count >= cap) deny(`${w.groups[group_id].name} is full (${count} of ${cap} men)`);
  }
  return { ok: true, demo: true };
}

/**
 * Bulk invite, demo edition. No email is sent and nobody is added to the
 * world — a prospect gets the real flow (preview, per-row results, capacity
 * refusals) without side effects. The last row is failed deliberately so the
 * partial-failure path is visible rather than theoretical.
 */
export async function inviteMany({ people = [], group_id }) {
  await new Promise((r) => setTimeout(r, 900));
  const { w } = caller();
  const team = w.groups[group_id];
  const used = Object.values(w.people)
    .filter((p) => p.group_id === group_id && !p.is_suspended).length;
  let remaining = Math.max(0, (team?.max_members ?? 15) - used);

  const results = people.map((p, i) => {
    if (remaining <= 0) {
      return { email: p.email, ok: false, code: "team_full",
        error: `${team?.name ?? "That team"} is full` };
    }
    if (i === people.length - 1 && people.length > 2) {
      return { email: p.email, ok: false, code: "duplicate_email",
        error: "this man already has an account (sample failure)" };
    }
    remaining--;
    return { email: p.email, ok: true, user_id: `demo-${Math.random().toString(36).slice(2, 8)}` };
  });

  return {
    ok: results.every((r) => r.ok),
    sent: results.filter((r) => r.ok).length,
    failed: results.filter((r) => !r.ok).length,
    remaining,
    demo: true,
    results,
  };
}

/* ---------- account recovery, demo edition -------------------------------- */

// A prospect walking through /demo should see the recovery path exactly as a
// leader would, including the case it was built for: a man who was invited and
// never accepted. The fictional world has no auth records, so the status is
// derived from the persona ids — deterministically, so the roster does not
// reshuffle every render.
export async function loadAccessStatus() {
  const { w, id } = caller();
  const out = {};
  let n = 0;
  for (const p of Object.values(w.people)) {
    if (p.id === id) continue;
    n++;
    // Every third man is shown as never having accepted. That is roughly the
    // real ratio the ministry hit, and it is the state worth demonstrating.
    const stuck = n % 3 === 0;
    out[p.id] = {
      id: p.id,
      email: `${p.name.toLowerCase().replace(/[^a-z]+/g, ".")}@example.com`,
      confirmed: !stuck,
      invited_at: "2026-07-04T14:02:00.000Z",
      last_sign_in_at: stuck ? null : "2026-08-11T12:40:00.000Z",
    };
  }
  return out;
}

export async function resendForMan(userId) {
  await new Promise((r) => setTimeout(r, 600));
  const { w } = caller();
  const p = w.people[userId];
  if (!p) deny("you cannot manage that man");
  return {
    ok: true, demo: true, mode: "invite",
    message: `Demo: no email was really sent. In the live app a fresh link would be on its way to ${p.name}.`,
  };
}

export async function setTempPassword(userId) {
  await new Promise((r) => setTimeout(r, 600));
  const { w, id } = caller();
  if (rankOf(w.people[id]) < 3) deny("only an Admin or Super Admin can set another man's password");
  const p = w.people[userId];
  if (!p) deny("you cannot manage that man");
  return {
    ok: true, demo: true,
    message: `Demo: nothing was changed. In the live app ${p.name} could now sign in with that password.`,
  };
}
